// frontend\src\components\analytics\AnalyticsInventoryValue.tsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../../utils/api';
import LoadingSpinner from '../LoadingSpinner';

interface ValueRow {
  warehouse: string;
  currency: string;
  value: number;
  units: number;
  units_without_cost: number;
}

interface InventoryValueResponse {
  rows: ValueRow[];
  totalUnits: number;
  totalUnitsWithoutCost: number;
}

// USD per 1 unit of currency; used only if the live rate fetch fails
const FALLBACK_RATES: { [currency: string]: number } = { USD: 1, CAD: 0.73, EUR: 1.08 };

const WAREHOUSE_COLORS = ['#E07856', '#4FA37C', '#8E6FC9', '#E3B341', '#56A6C9', '#D8689B', '#7A9E5F', '#7B8794'];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

const AnalyticsInventoryValue: React.FC = () => {
  const [data, setData] = useState<InventoryValueResponse | null>(null);
  // Rates are expressed as USD per 1 unit of the source currency
  const [rates, setRates] = useState<{ [currency: string]: number } | null>(null);
  const [ratesLive, setRatesLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [valueRes, ratesRes] = await Promise.all([
          api.get('/inventory-value'),
          // ECB reference rates, no API key required. Quoted as 1 USD = x CAD/EUR.
          // Note: must use the .dev/v1 host — the old .app domain redirects
          // without CORS headers, which browsers block.
          axios
            .get('https://api.frankfurter.dev/v1/latest', { params: { base: 'USD', symbols: 'CAD,EUR' } })
            .catch(() => null),
        ]);
        setData(valueRes.data);

        const fxRates = ratesRes?.data?.rates;
        if (fxRates?.CAD && fxRates?.EUR) {
          // Invert: USD per 1 CAD / 1 EUR
          setRates({ USD: 1, CAD: 1 / fxRates.CAD, EUR: 1 / fxRates.EUR });
          setRatesLive(true);
        } else {
          setRates(FALLBACK_RATES);
          setRatesLive(false);
        }
      } catch (err: any) {
        console.error('Inventory value fetch error:', err);
        setError(err.response?.data?.error || 'Failed to fetch inventory value');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const toUSD = (value: number, currency: string) =>
    value * ((rates || FALLBACK_RATES)[currency] ?? 1);

  let content: React.ReactNode = null;
  if (data && rates) {
    // Aggregate converted USD value per warehouse
    const byWarehouse = new Map<string, { valueUSD: number; units: number; currencies: Set<string> }>();
    for (const row of data.rows) {
      const entry = byWarehouse.get(row.warehouse) || { valueUSD: 0, units: 0, currencies: new Set<string>() };
      entry.valueUSD += toUSD(row.value, row.currency);
      entry.units += row.units;
      if (row.value > 0) entry.currencies.add(row.currency);
      byWarehouse.set(row.warehouse, entry);
    }
    const warehouses = Array.from(byWarehouse.entries())
      .map(([warehouse, v]) => ({ warehouse, ...v }))
      .sort((a, b) => b.valueUSD - a.valueUSD);
    const totalUSD = warehouses.reduce((sum, w) => sum + w.valueUSD, 0);
    const maxValue = Math.max(1, ...warehouses.map((w) => w.valueUSD));

    content = (
      <>
        <div className="analytics-total-value">
          <div className="analytics-total-value-row">{formatCurrency(totalUSD)}</div>
          <div className="analytics-total-value-comparison">
            {formatNumber(data.totalUnits)} units at cost, converted to USD
          </div>
        </div>

        <p className="analytics-fx-rates">
          {ratesLive ? 'Live exchange rates' : 'Live rates unavailable — using approximate rates'}:{' '}
          1 CAD = {rates.CAD.toFixed(4)} USD · 1 EUR = {rates.EUR.toFixed(4)} USD
          {ratesLive && <span className="analytics-fx-source"> (ECB reference, loaded at page load)</span>}
        </p>

        {warehouses.length === 0 ? (
          <p className="analytics-empty-text">No stocked inventory found.</p>
        ) : (
          <div className="analytics-warehouse-list">
            {warehouses.map((w, i) => (
              <div className="analytics-warehouse-row wide-qty" key={w.warehouse}>
                <span
                  className="analytics-warehouse-name"
                  title={`${formatNumber(w.units)} units${w.currencies.size > 0 ? ` — costs in ${Array.from(w.currencies).join(', ')}` : ''}`}
                >
                  {w.warehouse}
                </span>
                <div className="analytics-warehouse-bar-track">
                  <div
                    className="analytics-warehouse-bar"
                    style={{
                      width: `${(w.valueUSD / maxValue) * 100}%`,
                      backgroundColor: WAREHOUSE_COLORS[i % WAREHOUSE_COLORS.length],
                    }}
                  />
                </div>
                <span className="analytics-warehouse-qty">{formatCurrency(w.valueUSD)}</span>
              </div>
            ))}
          </div>
        )}

        {data.totalUnitsWithoutCost > 0 && (
          <p className="analytics-value-caveat">
            {formatNumber(data.totalUnitsWithoutCost)} unit{data.totalUnitsWithoutCost === 1 ? '' : 's'} have no
            cost price set and are valued at $0 — set cost prices in the product details editor for full coverage.
          </p>
        )}
      </>
    );
  }

  return (
    <div className="analytics-card">
      <div className="analytics-card-header">
        <h3>Total Inventory Value by Warehouse</h3>
      </div>
      {loading && <LoadingSpinner />}
      {error && <div className="analytics-error-text">Error: {error}</div>}
      {!loading && !error && content}
    </div>
  );
};

export default AnalyticsInventoryValue;
